import { FC } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Autocomplete, TextField, TextFieldProps, Typography } from '@mui/material';
import { STATES } from '~/constants/states';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { validState } from '~/utils/validState';
import SvgIconStyle from '../SvgIconStyle';

type Props = {
  name: string;
  label: string;
};

const RHFStateSelect: FC<Props & TextFieldProps> = ({ name, label, ...other }) => {
  const { control, setValue, getValues } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          autoHighlight
          selectOnFocus
          clearOnEscape
          options={STATES}
          disabled={other.disabled}
          value={STATES.find((state) => state.value === getValues(field.name)) || null}
          defaultValue={STATES.filter((state) => state.value === getValues(field.name))[0]}
          getOptionLabel={(option) => option.label}
          onChange={(_event, newValue) => setValue(field.name, newValue?.value || '')}
          onBlur={(_event) => {
            if (!validState(getValues(field.name))) {
              setValue(field.name, '');
            }
          }}
          popupIcon={<SvgIconStyle src="/icons/ic_expand_more.svg" />}
          renderOption={(props, option, { inputValue }) => {
            const { label } = option;
            const matches = match(label, inputValue);
            const parts = parse(label, matches);

            return (
              <li {...props}>
                {parts.map((part, index) => (
                  <>
                    {index !== 0 && parts[index - 1].text.slice(-1) === ' ' && (
                      <Typography component="pre" variant="subtitle2" key={index}>
                        {' '}
                      </Typography>
                    )}
                    <Typography
                      key={part.text}
                      component="span"
                      variant="subtitle2"
                      fontWeight={part.highlight ? 700 : 400}
                    >
                      {part.text}
                    </Typography>
                    {index < parts.length - 1 && parts[index + 1].text.slice(0, 1) === ' ' && (
                      <Typography component="pre" variant="subtitle2" key={index + part.text}>
                        {' '}
                      </Typography>
                    )}
                  </>
                ))}
              </li>
            );
          }}
          renderInput={(params) => <TextField label={label} {...params} {...other} {...field} />}
        />
      )}
    />
  );
};

export default RHFStateSelect;
